import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Modal } from '@cognitiv/cyprus-ui';
import { BottomNavigation } from 'components/bottom-navigation/BottomNavigation';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectJob } from 'ducks/job/selectors';
import { selectJobDefinition } from 'ducks/job-definition/selectors';
import { selectJobDefinitionInputParameters } from 'ducks/job-definition-parameters/selectors';
import { runJobDefinition } from 'ducks/job-definitions/operators';
import { selectJobParameters } from 'ducks/job-parameters/selectors';
import { selectLocationGroupsList } from 'ducks/location-groups-list/selectors';
import { selectRunJobDefinition } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { selectProject } from 'ducks/project/selectors';
import { updateSettings } from 'ducks/settings/slices';
import { ProjectParameters } from 'ducks/types/store-types';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { useStepper } from 'modals/batches/hooks/usestepper';
import { default_input_parameter } from 'modals/constants';
import { JobDefinitionParameters } from 'modals/job-definition/components/JobDefinitionParameters';
import { RunJobDefinitionConfiguration } from 'modals/job-definition/components/RunJobDefinitionConfiguration';
import { default_job } from 'modals/job-definition/constants';
import { useJobDefinitionContext } from 'modals/job-definition/context/JobDefinitionContext';
import useForceUpdate from 'modals/parameters/components/hooks/useForceUpdate';
import { useNavigate } from 'react-router-dom';
import { handleError } from 'utils/errors';
import { getFormWarnings } from 'utils/helpers';
import { v4 as uuidv4 } from 'uuid';

import cn from 'modals/job-definition/components/Components.module.scss';

export const RunJobDefinitionModal = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { theme } = useSwitchTheme();

  const job = useAppSelector(selectJob);
  const job_definition = useAppSelector(selectJobDefinition);
  const input_parameters = useAppSelector(selectJobDefinitionInputParameters);
  const location_groups = useAppSelector(selectLocationGroupsList);
  const run_job_definition = useAppSelector(selectRunJobDefinition);
  const job_parameters = useAppSelector(selectJobParameters);
  const project = useAppSelector(selectProject);

  const { form, validate, errors, updateJobDefinitionFormDefault } = useJobDefinitionContext();

  const [inputs, setData] = useState<ProjectParameters>([]);
  const { page, reset, onNextStep, onPrevStep } = useStepper(0);

  const { force_update, handleUpdateDataFromModal } = useForceUpdate(setData);

  useEffect(() => {
    // modal is used to run job definitions and jobs
    // cant clear a job because you can rerun a job on the job page
    // switch statement can handle the useEffect
    if (!run_job_definition) return;

    const location_group = location_groups.find((lg) => lg.location_group_id === 1) || null;

    if (location_group) {
      switch (true) {
        case !!job.job_id:
          // need to set job definition id to null so that the correct POST route is used
          // need the location group to set just in case its not been configured
          // spread job
          updateJobDefinitionFormDefault({ ...location_group, ...job });
          break;
        case !!job_definition.job_definition_id:
          updateJobDefinitionFormDefault({
            ...location_group,
            ...job_definition,
          });
          break;
        default:
          updateJobDefinitionFormDefault({ ...location_group });
      }
    }

    setData([...(job.job_id ? job_parameters : input_parameters), { ...default_input_parameter, uuid: uuidv4() }]);
  }, [
    input_parameters,
    job,
    job.job_id,
    job_definition,
    job_parameters,
    location_groups,
    run_job_definition,
    updateJobDefinitionFormDefault,
  ]);

  const setParameters = useCallback(
    (row: ProjectParameters[number], force_update?: boolean) => {
      handleUpdateDataFromModal(row, force_update);
    },
    [handleUpdateDataFromModal],
  );

  const closeModal = useCallback(() => {
    reset();
    setData([]);
    dispatch(setModal({ run_job_definition: false }));
    updateJobDefinitionFormDefault(default_job);
  }, [dispatch, reset, updateJobDefinitionFormDefault]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const is_valid = validate();

    if (!is_valid) return;

    const { project_id } = project;
    try {
      if (!project_id) return;

      const res: any = await runJobDefinition(project_id, form, inputs);
      if (res?.error) return;

      closeModal();
      navigate(`/projects/${project_id}/jobs/all/${res}`);
    } catch (err: any) {
      handleError({ error: err?.message, ...err });
    }
  };

  const onNext = useCallback(() => {
    const is_valid = validate();

    if (!is_valid && errors.length) {
      dispatch(setModal({ warning: true }));
      dispatch(updateSettings({ warning: getFormWarnings(errors) }));
    }

    if (is_valid) {
      onNextStep();
    }
  }, [dispatch, errors, onNextStep, validate]);

  return (
    <>
      <Modal
        name="run_job_definition"
        show={run_job_definition}
        width={page === 0 ? 865 : 1200}
        padding={0}
        close_box={50}
        onClose={closeModal}
        standard={false}
      >
        <h2
          className={cn.header}
          style={{
            color: theme.font_color,
            backgroundColor: theme.element_background,
            borderBottom: theme.border,
          }}
        >
          Create Job
        </h2>
        <form className={cn.body} onSubmit={onSubmit} style={{ backgroundColor: theme.element_background }}>
          {page === 0 && <RunJobDefinitionConfiguration />}
          {page === 1 && (
            <JobDefinitionParameters force_update={force_update} setParameters={setParameters} parameters={inputs} />
          )}
          <BottomNavigation current={page} end={1} onBack={onPrevStep} onNext={onNext} onClose={closeModal} />
        </form>
      </Modal>
    </>
  );
};
